import type { InjectionKey, Ref } from 'vue'

// Confirmation dialog descriptor for "start a new chat". The host decides how
// to surface it (webview: native alert; web: RevDialog) and runs onAccept /
// onDecline accordingly.
export type NewChatDialog = {
  title: string
  description: string
  acceptButtonText: string
  declineButtonText: string
  onAccept: () => void
  onDecline?: (() => void) | undefined
}

type CommonHost = {
  // Inbound message pushed by the host (webview native 'chat'); '' when none.
  receivedEvent: Readonly<Ref<string>>
  clearReceivedEvent: () => void
  // Dismiss the assistant (webview: close webview; web: confirm then hide).
  close: () => void
  // Confirm, then start a new chat (webview: native alert; web: RevDialog).
  newChat: (dialog: NewChatDialog) => void
}

// Extensions provided only by the web host.
export type WebShoppingAssistantHost = CommonHost & {
  // Report whether the conversation holds a real user message, so the host can
  // gate destructive confirmations (skips the prompt on an empty chat).
  reportConversation: (hasUserMessages: boolean) => void
}

// Extensions provided only by the webview host.
export type WebviewShoppingAssistantHost = CommonHost & {
  // Signal the host the chat UI is ready (sends native 'ready').
  ready: () => void
  // Notify the host the cart changed (native bridge).
  addToCart: () => void
  // Reflect a freshly created chat id in the URL.
  syncChatId: (id: string) => void
  // Navigate to a fresh chat (route push).
  goToNewChat: () => void
}

/**
 * The seam between the route-agnostic shopping-assistant core and whatever
 * frames it. Each host (native webview, web floating container) provides its
 * own implementation; the core only ever talks to this type.
 */
export type ShoppingAssistantHost =
  | WebShoppingAssistantHost
  | WebviewShoppingAssistantHost

export const SHOPPING_ASSISTANT_HOST_KEY = Symbol(
  'shoppingAssistantHost',
) as InjectionKey<ShoppingAssistantHost>
